import { Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { selectStatus } from '../model/slice';
import { accessControlExhibitors, accessControlPage } from '../model/access-control';
import { composeUser, fallbackToPathname, getSettings } from '../model/utils';
import { NO_ACCESS_URL } from '../model/constants';
import { useGetPage } from '../model/hooks';
import {
  STATUS_ERROR, STATUS_LOADING, STATUS_READY, STATUS_UNINITIALIZED
} from '../../constants';
import { emptyNull, noop } from '../../../shared/misc';
import { useBookedTickets } from '../../../shared/m-anage';
import { useCoreUser } from '../../../shared/m-anage/hook/use-core-user';
import { selectIsPassThroughUser } from '../../../global-state/redux/selector/user-selector';
import { getEventId } from '../../../global-utils/vmFunctions/core';
import { useAccessMatrix } from '../model/use-access-matrix';
import { mapData } from '../../../global-utils/dataMapping';
import { useAddonSettings } from '../../../global-hooks/useAddonSettings';
import { useAppTimezone } from '../../../global-hooks/useAppTimezone';

const ComponentsArrRenderer = lazy(() => import('../../../components/core/ComponentsArrRenderer'));

const AccessControlWrapper = ({
  renderElement = noop,
  renderLoading = noop,
  renderError = noop,
  page = emptyNull,
  vmFunctions,
  ...restProps
}) => {
  const location = useLocation();
  const isPassThroughUser = useSelector(selectIsPassThroughUser);
  const isAdminClientPreview = useSelector((state) => state.appState?.isAdminClientPreview);
  const status = useSelector(selectStatus) ?? STATUS_UNINITIALIZED;
  const moduleConfig = getSettings();
  // IMPORTANT: call useBookedTickets() with no args so we share the same
  // SWR cache entry as AccessControlInit. Passing `moduleConfig` builds a
  // different SWR key (different schema-parsed input) and produces a second,
  // independent fetch that can stay pending while the init's already resolved,
  // leaving the wrapper stuck on its loading gate.
  const { data: btData, isReady: btIsReady, error: btError } = useBookedTickets();
  const { data: coreUserData, isReady: coreUserIsReady } = useCoreUser(moduleConfig);
  const { data: accessMatrix } = useAccessMatrix();
  const getPage = useGetPage();
  const { accessControl } = page;
  const accessControlMapped = accessControl
    ? mapData({ vmFunctions }, accessControl, emptyNull, {
      from: 'AccessControlWrapper:accessControl'
    })
    : undefined;
  const { identifier = fallbackToPathname(location) } = accessControlMapped ?? emptyNull;
  const loggedInOnlySettings = useAddonSettings('loggedInOnly');
  const isUserLoggedIn = useSelector((state) => !!state?.user?.login_info?.user?.id);
  const appTimezone = useAppTimezone();
  if (isPassThroughUser || isAdminClientPreview) return renderElement(page);
  if (status === STATUS_UNINITIALIZED || status === STATUS_LOADING) return renderLoading();
  if (status === STATUS_ERROR) return renderError();
  if (btError) return renderError();
  // For logged-in users, wait until BookedTickets has actually resolved
  // (data or error). `isLoading` alone is unreliable because SWR reports
  // `isLoading: false` whenever its key is null (e.g. token/eventId not yet
  // hydrated), which would let us evaluate access against an empty user.
  // We deliberately do NOT gate on CoreUser — it's a profile enrichment and
  // composeUser falls back to scanning tickets when it's absent.
  if (isUserLoggedIn && !btIsReady) return renderLoading();

  if (page?.route?.path === '/company-profile/:id' && getEventId() === 737) {
    // TODO: Should think about another solution in here. QUICK & DIRTY!!
    const identifierId = identifier.split('/')?.[2]; // extracting just the id
    const { result: companyProfileHasAccess, cause: companyProfileCause } = accessControlExhibitors(
      identifierId,
      accessMatrix,
      composeUser(btData, { loggedIn: isUserLoggedIn, extendedUser: coreUserData }),
      { timezone: appTimezone }
    );
    if (status === STATUS_READY && companyProfileHasAccess) return renderElement(page);
    return renderElement({ ...getPage(NO_ACCESS_URL), accessControlCause: companyProfileCause });
  }

  const { result: hasAccess, cause } = accessControlPage(
    identifier,
    accessMatrix,
    composeUser(btData, { loggedIn: isUserLoggedIn, extendedUser: coreUserData }),
    { timezone: appTimezone }
  );

  if (status === STATUS_READY && hasAccess) return renderElement(page);
  if (loggedInOnlySettings?.isActive && !isUserLoggedIn) {
    // MEV-9522 - if loggedInOnly addon is active and user is not logged in, we want to render the components from loggedInOnly addon instead of access denied page
    return (
      <Suspense fallback={renderLoading()}>
        <ComponentsArrRenderer
          vmFunctions={vmFunctions}
          components={loggedInOnlySettings?.components}
          parent={['PAGE', 'AccessControlWrapper']}
          {...restProps}
        />
      </Suspense>
    );
  }
  return renderElement({ ...getPage(NO_ACCESS_URL), accessControlCause: cause });
};

export default AccessControlWrapper;
